import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Spinner,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  FormHelperText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { FiTrash2, FiEdit2, FiPlus, FiMapPin } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { getCoordinatesForAddress } from '../../services/geocoding';

const FiMapPinIcon = FiMapPin as React.ElementType;

// Cast icons as React.ElementType to fix TypeScript issues
const FiTrash2Icon = FiTrash2 as React.ElementType;
const FiEdit2Icon = FiEdit2 as React.ElementType;
const FiPlusIcon = FiPlus as React.ElementType;

export interface ServiceAreaCenter {
  name: string;
  latitude: number;
  longitude: number;
  radiusMiles: number;
}

export interface ServiceAreaPolygon {
  name: string;
  points: { lat: number; lng: number }[];
}

interface ServiceAreaSettings {
  serviceAreas: ServiceAreaCenter[];
  servicePolygons?: ServiceAreaPolygon[];
  freeDeliveryRadius: number;
  travelFeePerMile: number;
}

export interface AppSettings {
  id: string;
  paymentStrategy?: any;
  businessHours?: any;
  bookingConstraints?: any;
  serviceAreaSettings?: ServiceAreaSettings;
  bookingPoolSettings?: any;
  holidays?: any[];
  technicianSchedules?: any;
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const DEFAULT_SERVICE_AREA: ServiceAreaSettings = {
  serviceAreas: [],
  freeDeliveryRadius: 5,
  travelFeePerMile: 2.50,
};

const ServiceAreaSettingsComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<ServiceAreaSettings>({
    serviceAreas: [],
    servicePolygons: [],
    freeDeliveryRadius: 5,
    travelFeePerMile: 2.50,
  });
  
  // Service Center modals
  const { isOpen: isCenterOpen, onOpen: onCenterOpen, onClose: onCenterClose } = useDisclosure();
  const [editingCenterIndex, setEditingCenterIndex] = useState<number | null>(null);
  
  // Service Polygon modals
  const { isOpen: isPolygonOpen, onOpen: onPolygonOpen, onClose: onPolygonClose } = useDisclosure();
  const [editingPolygonIndex, setEditingPolygonIndex] = useState<number | null>(null);
  
  const [centerForm, setCenterForm] = useState<ServiceAreaCenter>({
    name: '',
    latitude: 0,
    longitude: 0,
    radiusMiles: 5,
  });
  
  const [addressForGeocode, setAddressForGeocode] = useState('');
  const [geocodingLoading, setGeocodingLoading] = useState(false);

  // Polygon form state
  const [polygonFormName, setPolygonFormName] = useState('');
  const [polygonPointsText, setPolygonPointsText] = useState(''); // lat,lng per line
  const [polygonAddress, setPolygonAddress] = useState('');

  // Mini-map inside polygon modal
  const miniMapContainerRef = useRef<HTMLDivElement | null>(null);
  const miniMapRef = useRef<any>(null);
  const miniPolygonRef = useRef<any>(null);
  const miniMarkersRef = useRef<any[]>([]);

  // Google Maps
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const drawingManagerRef = useRef<any>(null);
  const overlaysRef = useRef<any[]>([]);
  const [mapLoading, setMapLoading] = useState(true);

  function resolveMapsApiKey(): string {
    // Try direct key first
    // eslint-disable-next-line no-restricted-globals
    const direct = (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    if (direct) {
      console.log('[ServiceArea] Using direct REACT_APP_GOOGLE_MAPS_API_KEY');
      return direct;
    }
    
    // Determine environment: check localStorage first, then REACT_APP_ENV, then NODE_ENV
    const envFromStorage = typeof window !== 'undefined' ? (localStorage.getItem('selectedEnvironment') || '').toLowerCase() : '';
    const reactAppEnv = ((process as any).env.REACT_APP_ENV || '').toLowerCase();
    const nodeEnv = ((process as any).env.NODE_ENV || '').toLowerCase();
    
    // Use localStorage preference, or build-time env, or NODE_ENV as fallback
    let buildEnv = envFromStorage || reactAppEnv;
    
    // If no explicit REACT_APP_ENV, use NODE_ENV (Vercel sets this automatically)
    if (!buildEnv && nodeEnv === 'production') {
      buildEnv = 'production';
    } else if (!buildEnv) {
      buildEnv = 'development';
    }
    
    const envName = buildEnv.toLowerCase();
    const byEnv: Record<string, string | undefined> = {
      development: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_DEV,
      preview: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_PREVIEW,
      production: (process as any).env.REACT_APP_GOOGLE_MAPS_API_KEY_PROD,
    };
    
    const selectedKey = byEnv[envName];
    console.log('[ServiceArea] Resolving Maps API Key:', { 
      envFromStorage, 
      reactAppEnv, 
      nodeEnv, 
      buildEnv, 
      envName, 
      hasKey: !!selectedKey,
      keyPreview: selectedKey ? `${selectedKey.slice(0, 10)}...${selectedKey.slice(-4)}` : 'none'
    });
    
    return selectedKey || '';
  }

  const loadGoogleMaps = async () => {
    if (typeof window === 'undefined') return;
    // Already loaded
    // @ts-ignore
    if (window.google && window.google.maps) {
      setMapLoading(false);
      initMap();
      return;
    }
    const key = resolveMapsApiKey();
    if (!key) {
      setMapLoading(false);
      toast({ title: 'Maps Key Missing', description: 'Google Maps API key is not configured', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const existing = document.getElementById('gmaps-js');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        // If script tag exists, avoid double-inject; let callback/init handle
        resolve();
        return;
      }
      try {
        const masked = `${key.slice(0, 4)}...${key.slice(-4)}`;
        const envFromStorage = typeof window !== 'undefined' ? (localStorage.getItem('selectedEnvironment') || '').toLowerCase() : '';
        const buildEnv = ((process as any).env.REACT_APP_ENV || 'development').toLowerCase();
        const envName = envFromStorage || buildEnv;
        // Helpful diagnostics in dev console
        // eslint-disable-next-line no-console
        console.log('[Maps] Loading JS API', { envName, key: masked });
      } catch {}
      // Provide callbacks to capture auth and load states
      // @ts-ignore
      (window as any).gm_authFailure = () => {
        toast({ title: 'Maps Auth Error', description: 'API key unauthorized for this referrer or project', status: 'error', duration: 4000, isClosable: true });
      };
      // @ts-ignore
      (window as any).__initAdminServiceMap = () => {
        setMapLoading(false);
        initMap();
      };
      const script = document.createElement('script');
      script.id = 'gmaps-js';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=drawing&v=3.64,places&v=3.64&callback=__initAdminServiceMap`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps JS'));
      document.body.appendChild(script);
    });
    // Fallback init in case callback never fires (e.g., blocked callback param)
    setTimeout(() => {
      // @ts-ignore
      if (window.google && window.google.maps && !mapRef.current) {
        setMapLoading(false);
        initMap();
      }
    }, 2000);
    // When using callback, __initAdminServiceMap will handle init if not already initialized
  };

  const initMap = () => {
    if (!mapContainerRef.current) return;
    // @ts-ignore
    const gmaps = window.google && window.google.maps;
    if (!gmaps) return;
    const center = { lat: 33.7490, lng: -84.3880 };
    mapRef.current = new gmaps.Map(mapContainerRef.current, {
      center,
      zoom: 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    renderOverlays();
  };

  const clearOverlays = () => {
    overlaysRef.current.forEach(o => {
      if (o && typeof o.setMap === 'function') {
        o.setMap(null);
      }
    });
    overlaysRef.current = [];
  };

  const renderOverlays = () => {
    // @ts-ignore
    const gmaps = window.google && window.google.maps;
    if (!gmaps || !mapRef.current) return;
    clearOverlays();
    const bounds = new gmaps.LatLngBounds();

    // Centers as circles
    (settings.serviceAreas || []).forEach(center => {
      const pos = new gmaps.LatLng(center.latitude, center.longitude);
      const circle = new gmaps.Circle({
        map: mapRef.current,
        center: pos,
        radius: (center.radiusMiles || 0) * 1609.34,
        strokeColor: '#1E88E5',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#1E88E5',
        fillOpacity: 0.08,
      });
      overlaysRef.current.push(circle);
      bounds.extend(pos);
    });

    // Polygons
    (settings.servicePolygons || []).forEach(poly => {
      const path = (poly.points || []).map(p => ({ lat: p.lat, lng: p.lng }));
      if (path.length === 0) return;
      const polygon = new gmaps.Polygon({
        map: mapRef.current,
        paths: path,
        strokeColor: '#E53935',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#E53935',
        fillOpacity: 0.08,
      });
      overlaysRef.current.push(polygon);
      path.forEach(pt => bounds.extend(pt));
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }
  };

  const handleFitBounds = () => {
    renderOverlays();
  };

  const handleDrawPolygon = () => {
    // @ts-ignore
    const gmaps = window.google && window.google.maps;
    if (!gmaps || !mapRef.current) {
      toast({ title: 'Map Unavailable', description: 'Map is not ready yet', status: 'error', duration: 2000, isClosable: true });
      return;
    }
    // @ts-ignore
    if (!gmaps.drawing) {
      toast({ title: 'Drawing Library Missing', description: 'Enable the Drawing library in the Maps JS load (libraries=drawing)', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setMap(null);
      drawingManagerRef.current = null;
    }
    const dm = new gmaps.drawing.DrawingManager({
      drawingMode: gmaps.drawing.OverlayType.POLYGON,
      drawingControl: false,
      polygonOptions: {
        strokeColor: '#43A047',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#43A047',
        fillOpacity: 0.1,
        editable: true,
      },
    });
    drawingManagerRef.current = dm;
    dm.setMap(mapRef.current);
    gmaps.event.addListener(dm, 'polygoncomplete', (polygon: any) => {
      const path = polygon.getPath().getArray();
      const points = path.map((latLng: any) => ({ lat: latLng.lat(), lng: latLng.lng() }));
      // Pre-fill modal fields
      setEditingPolygonIndex(null);
      setPolygonFormName('');
      setPolygonPointsText(points.map((p: { lat: number; lng: number }) => `${p.lat},${p.lng}`).join('\n'));
      onPolygonOpen();
      // Remove drawing overlay; it will be re-rendered after save
      polygon.setMap(null);
      dm.setMap(null);
      drawingManagerRef.current = null;
    });
  };

  useEffect(() => {
    loadGoogleMaps();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Initialize map once script is loaded
    if (!mapLoading) {
      // @ts-ignore
      if (window.google && window.google.maps && mapContainerRef.current && !mapRef.current) {
        initMap();
      }
    }
  }, [mapLoading]);

  useEffect(() => {
    // Re-render overlays when settings change
    // @ts-ignore
    if (window.google && window.google.maps && mapRef.current) {
      renderOverlays();
    }
  }, [settings]);

  // Initialize mini map when polygon modal opens
  useEffect(() => {
    if (isPolygonOpen) {
      // Delay a tick to ensure modal DOM is ready
      setTimeout(() => {
        // @ts-ignore
        if (window.google && window.google.maps && miniMapContainerRef.current) {
          initMiniMap();
        } else {
          // If Google Maps isn't ready yet, trigger loader and try again
          loadGoogleMaps().then(() => {
            setTimeout(() => {
              // @ts-ignore
              if (window.google && window.google.maps && miniMapContainerRef.current) {
                initMiniMap();
              }
            }, 200);
          });
        }
      }, 50);
    } else {
      // Clean up mini map overlays
      clearMiniOverlays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolygonOpen]);

  // Re-render mini overlays when text changes
  useEffect(() => {
    // @ts-ignore
    if (window.google && window.google.maps && miniMapRef.current) {
      renderMiniOverlays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygonPointsText]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as AppSettings;
        if (data.serviceAreaSettings) {
          setSettings({
            ...DEFAULT_SERVICE_AREA,
            servicePolygons: [],
            ...data.serviceAreaSettings,
          });
        } else {
          setSettings({ ...DEFAULT_SERVICE_AREA, servicePolygons: [] });
        }
      } else {
        setSettings({ ...DEFAULT_SERVICE_AREA, servicePolygons: [] });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load service area settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setSettings(DEFAULT_SERVICE_AREA);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          serviceAreaSettings: settings,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          paymentStrategy: 'collect_full_at_booking',
          serviceAreaSettings: settings,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Service area settings updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save service area settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Geocoding function
  const handleGeocode = async () => {
    if (!addressForGeocode.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an address',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setGeocodingLoading(true);
    try {
      const coords = await getCoordinatesForAddress(addressForGeocode);
      setCenterForm(prev => ({
        ...prev,
        latitude: coords.latitude,
        longitude: coords.longitude,
      }));
      toast({
        title: 'Success',
        description: 'Address geocoded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Geocoding Error',
        description: error instanceof Error ? error.message : 'Failed to geocode address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setGeocodingLoading(false);
    }
  };

  // Service Center handlers
  const handleAddCenter = () => {
    setEditingCenterIndex(null);
    setCenterForm({ name: '', latitude: 0, longitude: 0, radiusMiles: 5 });
    setAddressForGeocode('');
    onCenterOpen();
  };

  const handleEditCenter = (index: number) => {
    setEditingCenterIndex(index);
    setCenterForm((settings.serviceAreas || [])[index]);
    onCenterOpen();
  };

  const handleSaveCenter = () => {
    if (!centerForm.name || centerForm.latitude === 0 || centerForm.longitude === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields and geocode an address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newAreas = [...(settings.serviceAreas || [])];
    if (editingCenterIndex !== null) {
      newAreas[editingCenterIndex] = centerForm;
    } else {
      newAreas.push(centerForm);
    }
    
    setSettings(prev => ({
      ...prev,
      serviceAreas: newAreas,
    }));

    onCenterClose();
    toast({
      title: 'Success',
      description: editingCenterIndex !== null ? 'Service center updated' : 'Service center added',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDeleteCenter = (index: number) => {
    const newAreas = (settings.serviceAreas || []).filter((_, i) => i !== index);
    setSettings(prev => ({
      ...prev,
      serviceAreas: newAreas,
    }));
  };

  // Polygon handlers
  const handleAddPolygon = () => {
    setEditingPolygonIndex(null);
    setPolygonFormName('');
    setPolygonPointsText('');
    onPolygonOpen();
  };

  const handleEditPolygon = (index: number) => {
    setEditingPolygonIndex(index);
    const poly = (settings.servicePolygons || [])[index];
    setPolygonFormName(poly?.name || '');
    const text = (poly?.points || []).map(p => `${p.lat},${p.lng}`).join('\n');
    setPolygonPointsText(text);
    onPolygonOpen();
  };

  const handleDeletePolygon = (index: number) => {
    const next = (settings.servicePolygons || []).filter((_, i) => i !== index);
    setSettings(prev => ({ ...prev, servicePolygons: next }));
  };

  const handleSavePolygon = () => {
    const name = polygonFormName.trim();
    if (!name) {
      toast({ title: 'Validation', description: 'Polygon name is required', status: 'error', duration: 2000, isClosable: true });
      return;
    }
    const lines = polygonPointsText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const points = lines.map((line, idx) => {
      const [latStr, lngStr] = line.split(',').map(s => s.trim());
      const lat = Number(latStr);
      const lng = Number(lngStr);
      if (!isFinite(lat) || !isFinite(lng)) {
        throw new Error(`Invalid coordinate on line ${idx + 1}. Use: lat,lng`);
      }
      return { lat, lng };
    });
    if (points.length < 3) {
      toast({ title: 'Validation', description: 'Polygon must have at least 3 points', status: 'error', duration: 2000, isClosable: true });
      return;
    }
    try {
      const next = [...(settings.servicePolygons || [])];
      const poly = { name, points } as ServiceAreaPolygon;
      if (editingPolygonIndex !== null) {
        next[editingPolygonIndex] = poly;
      } else {
        next.push(poly);
      }
      setSettings(prev => ({ ...prev, servicePolygons: next }));
      onPolygonClose();
      toast({ title: 'Success', description: editingPolygonIndex !== null ? 'Polygon updated' : 'Polygon added', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed to parse polygon points', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const clearMiniOverlays = () => {
    // Clear markers
    miniMarkersRef.current.forEach(m => m && typeof m.setMap === 'function' && m.setMap(null));
    miniMarkersRef.current = [];
    // Clear polygon
    if (miniPolygonRef.current && typeof miniPolygonRef.current.setMap === 'function') {
      miniPolygonRef.current.setMap(null);
      miniPolygonRef.current = null;
    }
  };

  const renderMiniOverlays = () => {
    // @ts-ignore
    const gmaps = window.google && window.google.maps;
    if (!gmaps || !miniMapRef.current) return;
    clearMiniOverlays();
    const points = polygonPointsText
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(Boolean)
      .map(l => {
        const [latStr, lngStr] = l.split(',').map(s => s.trim());
        return { lat: Number(latStr), lng: Number(lngStr) };
      })
      .filter(p => isFinite(p.lat) && isFinite(p.lng));

    const bounds = new gmaps.LatLngBounds();
    points.forEach(p => {
      const marker = new gmaps.Marker({ map: miniMapRef.current, position: p });
      miniMarkersRef.current.push(marker);
      bounds.extend(p);
    });
    if (points.length >= 3) {
      miniPolygonRef.current = new gmaps.Polygon({
        map: miniMapRef.current,
        paths: points,
        strokeColor: '#E53935',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#E53935',
        fillOpacity: 0.08,
      });
    }
    if (!bounds.isEmpty()) {
      miniMapRef.current.fitBounds(bounds);
    }
  };

  const initMiniMap = () => {
    // @ts-ignore
    const gmaps = window.google && window.google.maps;
    if (!gmaps || !miniMapContainerRef.current) return;
    const center = { lat: 33.7490, lng: -84.3880 };
    miniMapRef.current = new gmaps.Map(miniMapContainerRef.current, {
      center,
      zoom: 12,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });
    // Click to add point
    miniMapRef.current.addListener('click', (e: any) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const next = (polygonPointsText ? polygonPointsText + '\n' : '') + `${lat},${lng}`;
      setPolygonPointsText(next);
      // Render will occur via effect below
    });
    renderMiniOverlays();
  };

  const handleMiniGeocode = async () => {
    if (!polygonAddress.trim()) {
      toast({ title: 'Error', description: 'Enter an address to pan', status: 'error', duration: 2000, isClosable: true });
      return;
    }
    try {
      const coords = await getCoordinatesForAddress(polygonAddress.trim());
      // @ts-ignore
      const gmaps = window.google && window.google.maps;
      if (gmaps && miniMapRef.current) {
        miniMapRef.current.panTo({ lat: coords.latitude, lng: coords.longitude });
        miniMapRef.current.setZoom(14);
      }
    } catch (e) {
      toast({ title: 'Geocoding Error', description: e instanceof Error ? e.message : 'Failed to geocode', status: 'error', duration: 3000, isClosable: true });
    }
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          Service Area Management
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Define geographic service areas where you provide services.
              Configure delivery zones, travel fees, and coverage radius.
            </Text>
          </Box>
        </Alert>
      </Box>

      {/* Map Overview */}
      <Card mb={6}>
        <CardBody>
          <HStack justify="space-between" mb={3}>
            <Heading size="md">Map</Heading>
            <HStack>
              <Button size="sm" onClick={handleFitBounds}>Fit Bounds</Button>
              <Button size="sm" colorScheme="green" onClick={handleDrawPolygon}>Draw Polygon</Button>
            </HStack>
          </HStack>
          <Box position="relative" borderRadius="md" overflow="hidden" borderWidth="1px">
            {mapLoading && (
              <HStack p={4}>
                <Spinner size="sm" />
                <Text>Loading map…</Text>
              </HStack>
            )}
            <Box ref={mapContainerRef} id="service-area-map" style={{ width: '100%', height: 420 }} />
          </Box>
        </CardBody>
      </Card>

      <Card mb={6}>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="md">Service Centers (Radius-Based)</Heading>
                <Button
                  leftIcon={<FiPlusIcon />}
                  colorScheme="blue"
                  size="sm"
                  onClick={handleAddCenter}
                >
                  Add Service Center
                </Button>
              </HStack>

              {(settings.serviceAreas || []).length === 0 ? (
                <Alert status="info">
                  <AlertIcon />
                  No service areas defined. Click "Add Service Area" to create one.
                </Alert>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Latitude</Th>
                        <Th>Longitude</Th>
                        <Th>Radius (Miles)</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(settings.serviceAreas || []).map((area, index) => (
                        <Tr key={index}>
                          <Td fontWeight="medium">{area.name}</Td>
                          <Td>{area.latitude}</Td>
                          <Td>{area.longitude}</Td>
                          <Td>{area.radiusMiles}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <IconButton
                                aria-label="Edit"
                                icon={<FiEdit2Icon />}
                                size="sm"
                                onClick={() => handleEditCenter(index)}
                              />
                              <IconButton
                                aria-label="Delete"
                                icon={<FiTrash2Icon />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => handleDeleteCenter(index)}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl>
                <FormLabel>Free Delivery Radius (Miles)</FormLabel>
                <NumberInput
                  value={settings.freeDeliveryRadius}
                  onChange={(_, val) => setSettings(prev => ({ ...prev, freeDeliveryRadius: val }))}
                  min={0}
                  max={50}
                  precision={1}
                  step={0.5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Distance from service area center for free delivery
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Travel Fee Per Mile ($)</FormLabel>
                <NumberInput
                  value={settings.travelFeePerMile}
                  onChange={(_, val) => setSettings(prev => ({ ...prev, travelFeePerMile: val }))}
                  min={0}
                  max={10}
                  precision={2}
                  step={0.10}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Additional fee charged per mile beyond free delivery radius
                </FormHelperText>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

    {/* Polygons */}
    <Card mb={6}>
      <CardBody>
        <VStack spacing={6} align="stretch">
          <Box>
            <HStack justify="space-between" mb={4}>
              <Heading size="md">Service Polygons</Heading>
              <Button
                leftIcon={<FiPlusIcon />}
                colorScheme="blue"
                size="sm"
                onClick={handleAddPolygon}
              >
                Add Polygon
              </Button>
            </HStack>

            {(settings.servicePolygons || []).length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                No service polygons defined. Click "Add Polygon" to create one.
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Points</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(settings.servicePolygons || []).map((poly, index) => (
                      <Tr key={index}>
                        <Td fontWeight="medium">{poly.name}</Td>
                        <Td>{poly.points?.length || 0}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Edit"
                              icon={<FiEdit2Icon />}
                              size="sm"
                              onClick={() => handleEditPolygon(index)}
                            />
                            <IconButton
                              aria-label="Delete"
                              icon={<FiTrash2Icon />}
                              size="sm"
                              colorScheme="red"
                              onClick={() => handleDeletePolygon(index)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </VStack>
      </CardBody>
    </Card>

      <HStack spacing={3}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving"
        >
          Save Service Area Settings
        </Button>
      </HStack>

      {/* Add/Edit Service Center Modal */}
      <Modal isOpen={isCenterOpen} onClose={onCenterClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCenterIndex !== null ? 'Edit Service Center' : 'Add Service Center'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Center Name</FormLabel>
                <Input
                  value={centerForm.name}
                  onChange={(e) => setCenterForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Downtown Atlanta"
                />
              </FormControl>

              <Alert status="info" fontSize="sm">
                <AlertIcon />
                <Text fontSize="sm">Enter an address below and click "Geocode Address" to automatically fill coordinates, or enter coordinates manually.</Text>
              </Alert>

              <FormControl>
                <FormLabel>Address (for Geocoding)</FormLabel>
                <HStack>
                  <Input
                    value={addressForGeocode}
                    onChange={(e) => setAddressForGeocode(e.target.value)}
                    placeholder="Enter address or city, e.g., Atlanta, GA"
                  />
                  <Button
                    onClick={handleGeocode}
                    isLoading={geocodingLoading}
                    loadingText="..."
                    leftIcon={<FiMapPinIcon />}
                  >
                    Geocode
                  </Button>
                </HStack>
                <FormHelperText>Enter an address and click geocode to get coordinates</FormHelperText>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Latitude</FormLabel>
                <NumberInput
                  value={centerForm.latitude}
                  onChange={(_, val) => setCenterForm(prev => ({ ...prev, latitude: val }))}
                  precision={6}
                  step={0.000001}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Longitude</FormLabel>
                <NumberInput
                  value={centerForm.longitude}
                  onChange={(_, val) => setCenterForm(prev => ({ ...prev, longitude: val }))}
                  precision={6}
                  step={0.000001}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Service Radius (Miles)</FormLabel>
                <NumberInput
                  value={centerForm.radiusMiles}
                  onChange={(_, val) => setCenterForm(prev => ({ ...prev, radiusMiles: val }))}
                  min={1}
                  max={50}
                  precision={1}
                  step={0.5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCenterClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveCenter}>
              {editingCenterIndex !== null ? 'Update' : 'Add'} Center
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add/Edit Service Polygon Modal */}
      <Modal isOpen={isPolygonOpen} onClose={onPolygonClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingPolygonIndex !== null ? 'Edit Service Polygon' : 'Add Service Polygon'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Polygon Name</FormLabel>
                <Input
                  value={polygonFormName}
                  onChange={(e) => setPolygonFormName(e.target.value)}
                  placeholder="e.g., Downtown Core"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Pan Map by Address</FormLabel>
                <HStack>
                  <Input
                    value={polygonAddress}
                    onChange={(e) => setPolygonAddress(e.target.value)}
                    placeholder="Enter address or city"
                  />
                  <Button onClick={handleMiniGeocode} leftIcon={<FiMapPinIcon />}>Geocode</Button>
                </HStack>
              </FormControl>

              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Box ref={miniMapContainerRef} style={{ width: '100%', height: 300 }} />
              </Box>

              <FormControl isRequired>
                <FormLabel>Points (lat,lng per line)</FormLabel>
                <Input
                  as="textarea"
                  rows={8}
                  value={polygonPointsText}
                  onChange={(e) => setPolygonPointsText(e.target.value)}
                  placeholder={"33.7490,-84.3880\n33.7500,-84.3900\n33.7480,-84.3920"}
                />
                <FormHelperText>Enter at least 3 points. Example: 33.7490,-84.3880</FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPolygonClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSavePolygon}>
              {editingPolygonIndex !== null ? 'Update' : 'Add'} Polygon
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ServiceAreaSettingsComponent;

